import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { SectionHead, Reveal } from "@/components/Reveal";

const INDUSTRIES = ["Property & estates", "Golf & hospitality", "Trades & construction", "Professional services", "Health & fitness", "E-commerce & retail", "Other local business"];

const fmt = (v) => "£" + Math.round(v).toLocaleString("en-GB");

function SimSlider({ label, val, set, min, max, step, format, testid }) {
    return (
        <div>
            <div className="mb-3 flex items-baseline justify-between">
                <label className="text-[11px] font-bold uppercase tracking-[0.2em] text-white/45">{label}</label>
                <span className="font-mono text-sm text-white">{format(val)}</span>
            </div>
            <input
                type="range"
                min={min} max={max} step={step} value={val}
                onChange={(e) => set(Number(e.target.value))}
                className="hights-range w-full"
                data-testid={testid}
                aria-label={label}
            />
        </div>
    );
}

export default function Simulator() {
    const [industry, setIndustry] = useState(INDUSTRIES[0]);
    const [visitors, setVisitors] = useState(1200);
    const [enquiries, setEnquiries] = useState(24);
    const [conversion, setConversion] = useState(15);
    const [value, setValue] = useState(1200);

    const r = useMemo(() => {
        const current = enquiries * (conversion / 100) * value;
        const withCapture = enquiries * 1.2;
        const withConversion = conversion + 5;
        const scenario = withCapture * (withConversion / 100) * value * 1.1;
        return {
            current,
            scenario,
            annual: (scenario - current) * 12,
            steps: [
                { label: "Enquiries captured today", val: `${enquiries} / month` },
                { label: "+ Instant response recovers ~20% that currently slip", val: `${withCapture.toFixed(1)} enquiries` },
                { label: `+ Conversion ${conversion}% → ${withConversion}% (faster, clearer journey)`, val: `${(withCapture * (withConversion / 100)).toFixed(1)} customers` },
                { label: "+ ~10% more kept bookings via automated follow-up", val: `${(withCapture * (withConversion / 100) * 1.1).toFixed(1)} customers` },
            ],
        };
    }, [enquiries, conversion, value]);

    const max = Math.max(r.current, r.scenario) || 1;

    const Slider = ({ label, val, set, min, max, step, format, testid }) => (
        <div>
            <div className="mb-3 flex items-baseline justify-between">
                <label className="text-[11px] font-bold uppercase tracking-[0.2em] text-white/45">{label}</label>
                <span className="font-mono text-sm text-white">{format(val)}</span>
            </div>
            <input
                type="range"
                min={min} max={max} step={step} value={val}
                onChange={(e) => set(Number(e.target.value))}
                className="hights-range w-full"
                data-testid={testid}
                aria-label={label}
            />
        </div>
    );

    return (
        <section id="simulator" className="relative border-t border-white/10 py-28 md:py-36" data-testid="simulator-section">
            <div className="mx-auto max-w-[1400px] px-5 md:px-10">
                <SectionHead
                    number="05"
                    label="Growth simulator"
                    title="Run the numbers on a connected system."
                    copy="Adjust the inputs to your business. The scenario applies conservative, transparent assumptions — instant enquiry response, a modest conversion uplift and automated follow-up — and shows you exactly how it's calculated."
                />
                <div className="mt-16 grid grid-cols-1 gap-6 lg:grid-cols-12">
                    <Reveal className="lg:col-span-5">
                        <div className="space-y-8 border border-white/12 bg-surface p-8 md:p-10">
                            <div>
                                <label className="mb-3 block text-[11px] font-bold uppercase tracking-[0.2em] text-white/45">Industry</label>
                                <select
                                    value={industry}
                                    onChange={(e) => setIndustry(e.target.value)}
                                    data-testid="simulator-industry"
                                    className="h-12 w-full border border-white/15 bg-black px-4 text-sm text-white focus:border-white/45 focus:outline-none"
                                >
                                    {INDUSTRIES.map((i) => <option key={i}>{i}</option>)}
                                </select>
                            </div>
                            <Slider label="Monthly website visitors" val={visitors} set={setVisitors} min={100} max={20000} step={100} format={(v) => v.toLocaleString()} testid="simulator-visitors" />
                            <Slider label="Current monthly enquiries" val={enquiries} set={setEnquiries} min={2} max={300} step={1} format={(v) => v} testid="simulator-enquiries" />
                            <Slider label="Enquiry → customer rate" val={conversion} set={setConversion} min={2} max={60} step={1} format={(v) => `${v}%`} testid="simulator-conversion" />
                            <Slider label="Average customer value" val={value} set={setValue} min={50} max={10000} step={50} format={fmt} testid="simulator-value" />
                        </div>
                    </Reveal>
                    <Reveal delay={0.12} className="lg:col-span-7">
                        <div className="flex h-full flex-col border border-white/12 bg-surface p-8 md:p-10" data-testid="simulator-results">
                            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">Illustrative monthly revenue</p>
                            <div className="mt-8 space-y-6">
                                <div>
                                    <div className="mb-2 flex justify-between text-xs text-white/50"><span>Today</span><span className="font-mono text-white/80">{fmt(r.current)}</span></div>
                                    <div className="h-9 w-full bg-white/5">
                                        <motion.div className="h-full bg-white/25" animate={{ width: `${(r.current / max) * 100}%` }} transition={{ duration: 0.6 }} data-testid="simulator-bar-current" />
                                    </div>
                                </div>
                                <div>
                                    <div className="mb-2 flex justify-between text-xs text-white/50"><span>With a HIGHTS system</span><span className="font-mono text-white">{fmt(r.scenario)}</span></div>
                                    <div className="h-9 w-full bg-white/5">
                                        <motion.div className="h-full bg-white" animate={{ width: `${(r.scenario / max) * 100}%` }} transition={{ duration: 0.6 }} data-testid="simulator-bar-scenario" />
                                    </div>
                                </div>
                            </div>
                            <div className="mt-10 border-t border-white/10 pt-8">
                                <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-white/45">How it's calculated</p>
                                <ul className="mt-4 space-y-2.5">
                                    {r.steps.map((s, i) => (
                                        <li key={i} className="flex items-baseline justify-between gap-4 text-sm">
                                            <span className="font-light text-white/55">{s.label}</span>
                                            <span className="shrink-0 font-mono text-xs text-white/80">{s.val}</span>
                                        </li>
                                    ))}
                                </ul>
                                <p className="mt-6 font-display text-xl font-bold text-white" data-testid="simulator-annual">
                                    Illustrative annual difference: <span className="text-white">{fmt(r.annual)}</span>
                                </p>
                            </div>
                            <p className="mt-auto pt-8 text-[11px] font-light leading-relaxed text-white/35" data-testid="simulator-disclaimer">
                                Illustrative growth scenario — actual results vary by business, market and implementation.
                                Assumptions are deliberately conservative and shown in full above. No outcome is guaranteed.
                            </p>
                        </div>
                    </Reveal>
                </div>
            </div>
        </section>
    );
}
